<template>
  <div
    class="store border-b border-gray-400 px-6 py-8"
    :class="{
      'md:border-l-8 md:border-gray-400': isCurrent(store.id),
      'hover:cursor-pointer': !isCurrent(store.id),
    }"
    data-testid="ndl_search_card"
    @click="openStorePopup(store)"
  >
    <div class="flex flex-col gap-3">
      <div class="flex w-full justify-between">
        <div>
          <Headline tag="h5" look="h4" data-testid="store-finder-item-title">
            {{ headline }}
          </Headline>
        </div>
        <div class="flex shrink-0 items-start">
          <FimButton
            class="-mr-2 ml-2 mt-[-0.3rem]"
            type="tertiary"
            is-small
            :to="detailsUrl(store)"
            data-testid="store-finder-details-cta"
            @click.stop
          >
            {{ $t('store_finder.details') }}
            <template #iconAfter>
              <LazyIconFielmannArrowRight class="size-5" />
            </template>
          </FimButton>
        </div>
      </div>
      <div>
        <div>{{ store.street }}</div>
        <div>{{ `${store.zip} ${store.city}` }}</div>
        <div class="flex">
          <div class="flex flex-col justify-center">
            <LazyIconFielmannPin class="mr-1 size-4" />
          </div>
          <div class="flex flex-col justify-center">
            {{ store.distance.toFixed(2) }} km
          </div>
        </div>
        <StoreStatus :opening-details="store" />
      </div>
      <div class="mt-2">
        <Headline tag="h5" look="h4" class="mb-2">
          {{ $t('store_finder.title') }}
        </Headline>
        <div class="flex flex-col gap-2">
          <label :for="`appointment_options-${store.id}`">
            {{ $t('store_finder.appointment_option_label') }}
          </label>
          <StoreTermin :store="store" />
          <FimButton
            v-if="store.isRopo"
            is-small
            type="secondary"
            class="mt-1 w-full"
            data-testid="store-finder-ropo-cta"
            @click="gotoRopoBasket(store)"
          >
            <div class="flex">
              <span>
                {{ $t('ropo.ndl.list_cta') }}
              </span>
              <LazyIconFielmannArrowRight
                class="ml-1 mr-2 h-6 w-[18px] text-black"
              />
            </div>
          </FimButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Headline from '~/components/ui/headlines/Headline.vue'
import StoreStatus from '~/components/store-finder/StoreStatus.vue'
import StoreTermin from '~/components/store-finder/StoreTermin.vue'
import type { IStore } from '~/rpcMethods/stores'
import { useMapInfoWindow } from '~/composables/useMapInfoWindow'
import { useRopoBasketState } from '~/composables/useRopoBasketState'

const props = defineProps({
  store: {
    type: Object as PropType<IStore & { isRopo?: boolean }>,
    required: true,
  },
})

const { currentStore, setCurrentStore } = useMapInfoWindow()
const { $fimLocalePath, $i18n } = useNuxtApp()

const isCurrent = (storeId: number) => currentStore.value?.id === storeId

const openStorePopup = (store: IStore) => {
  if (window && window.innerWidth > 768) {
    setCurrentStore(store)
  }
}

const detailsUrl = (store: IStore) => {
  return getStoreUrl(store, $fimLocalePath)
}

const { gotoRopoBasket } = await useRopoBasketState()

const headline = computed(() => getStoreHeadline(props.store, $i18n))
</script>
